import React, { useContext, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import * as detaljerCss from './detaljer.module.scss';
import DatePicker from '../components/datepicker';
import Button from '../components/button';
import { parseIsoTzLess } from '../date.utils';
import translation from './activity.uha.i18n';
import {
  AktivitetskravDto,
  HttpError,
} from '../api/generated/studiested-generated-api';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { INGEN_AKTIVITETSKRAV } from './activity.uha';
import { useQuery } from '../api/useQuery';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import * as css from './activity.uha.module.scss';
import { ActivityTab } from './activityTabs.uha';
import {
  FakeLockedInput,
  TextInput,
  NumberInput,
  Textarea,
} from '../components/input';
import { ConfirmationDialog } from '../components/dialog';
import Dropdown from '../components/input/dropdown';
import { useActivityContextUHA } from './activitycontexts';
import { NotificationFunctionContext } from '../components/notifications/notificationProvider';
import PanelHeader from '../components/panelheader/panelHeader';
import { isNullOrUndefined } from '../util/isNullOrUndefined';
import useScrollIntoViewOnEvent from '../hooks/useScrollIntoViewOnEvent';
import FritekstSectionReadOnly from './common/fritekstsectionreadonly';
import dayjs from 'dayjs';
import { useFormatMessageFor } from '../i18n/translation';

const Detaljer = () => {
  const { praksisaktivitet, reloadPraksisaktivitet } = useActivityContextUHA();
  const navigate = useNavigate();
  const isCreating = !!useQuery().get('create');
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { addSuccessNotification, addErrorNotification } = useContext(
    NotificationFunctionContext,
  );

  const labels = useFormatMessageFor(translation);

  const {
    behov,
    krav,
    muligeKrav,
    sluttDato,
    startDato,
    klasse,
    fritekst: initFritekst,
  } = praksisaktivitet;

  const [dateFromIso, setDateFromIso] = useState<string | undefined>(startDato);
  const [dateToIso, setDateToIso] = useState<string | undefined>(
    isCreating ? undefined : sluttDato,
  );
  const [estimatedPlacesNeeded, setEstimatedPlacesNeeded] = useState(behov);
  const [aktivitestkrav, setAktivitestkrav] = useState<AktivitetskravDto | ''>(
    isCreating ? '' : krav || INGEN_AKTIVITETSKRAV,
  );
  const [klassenavn, setKlassenavn] = useState<string>(
    klasse.presentertNavn || klasse.bokstav,
  );
  const [fritekst, setFritekst] = useState<string>(initFritekst || '');

  const handleKravChanged = (kravId: string) => {
    if (kravId === INGEN_AKTIVITETSKRAV.id) {
      setAktivitestkrav(INGEN_AKTIVITETSKRAV);
    } else {
      const nyttKrav = muligeKrav.find((element) => element.id === kravId);
      if (nyttKrav !== undefined) {
        setAktivitestkrav(nyttKrav);
      }
    }
  };

  const deleteHandler = (showSnackbarMsg: boolean = true): void => {
    praksiskalenderApi
      .praksisaktivitetIdDelete({ pathParams: { id: praksisaktivitet.id } })
      .then(() => {
        if (showSnackbarMsg) {
          addSuccessNotification(labels.deleteSuccess);
        }
        navigate('/calendar');
      })
      .catch((err: HttpError) => addErrorNotification(err.body));
  };

  const saveHandler = (): void => {
    praksiskalenderApi
      .praksisaktivitetIdPut({
        pathParams: { id: praksisaktivitet.id },
        praksisaktivitetPutDto: {
          startDato: dateFromIso ? parseIsoTzLess(dateFromIso).getTime() : 0,
          sluttDato: dateToIso ? parseIsoTzLess(dateToIso).getTime() : 0,
          behov: estimatedPlacesNeeded ? estimatedPlacesNeeded : 0,
          aktivitetskravId: aktivitestkrav
            ? aktivitestkrav.id === '0'
              ? undefined
              : aktivitestkrav.id
            : undefined,
          klassenavn: klassenavn !== klasse.bokstav ? klassenavn : undefined,
          fritekst: fritekst?.trim(),
        },
      })
      .then(() => {
        addSuccessNotification(
          isCreating ? labels.saveSuccess : labels.changeSuccess,
        );
        reloadPraksisaktivitet();
        navigate(
          `/calendar/activity/${praksisaktivitet.id}/${ActivityTab.STEDER}`,
        );
      })
      .catch((err: HttpError) => addErrorNotification(err.body));
  };

  const estimatedPlacesChanged = useMemo(
    () => estimatedPlacesNeeded !== behov,
    [estimatedPlacesNeeded, behov],
  );

  const klassenavnChanged = useMemo(
    () => klassenavn !== (klasse.presentertNavn || klasse.bokstav),
    [klasse, klassenavn],
  );

  const periodChanged = useMemo(
    () =>
      dateFromIso != null &&
      dateToIso != null &&
      (dateFromIso !== startDato || dateToIso !== sluttDato),
    [dateFromIso, dateToIso, startDato, sluttDato],
  );

  const aktivitetskravChanged = useMemo(
    () =>
      aktivitestkrav !== '' &&
      ((!isNullOrUndefined(krav) && krav.id !== aktivitestkrav.id) ||
        (isNullOrUndefined(krav) &&
          aktivitestkrav.id !== INGEN_AKTIVITETSKRAV.id)),
    [aktivitestkrav, krav],
  );

  const fritekstChanged = useMemo(() => {
    const notEqualToInitFritekst = fritekst !== initFritekst;
    const notEmptyString = fritekst !== '';

    return isNullOrUndefined(initFritekst)
      ? notEqualToInitFritekst && notEmptyString
      : notEqualToInitFritekst;
  }, [fritekst, initFritekst]);

  const activityChanged = useMemo(
    () =>
      estimatedPlacesChanged ||
      klassenavnChanged ||
      periodChanged ||
      aktivitetskravChanged ||
      fritekstChanged,
    [
      estimatedPlacesChanged,
      klassenavnChanged,
      periodChanged,
      aktivitetskravChanged,
      fritekstChanged,
    ],
  );

  const endDateChanged = useMemo(
    () => dateToIso !== sluttDato,
    [dateToIso, sluttDato],
  );

  const opprettDisabled = useMemo(
    () =>
      isCreating &&
      (!endDateChanged || (muligeKrav.length > 0 && aktivitestkrav === '')),
    [aktivitestkrav, isCreating, endDateChanged, muligeKrav],
  );

  const praksisklasseChanged = useMemo(
    () => klassenavn !== (klasse.presentertNavn || klasse.bokstav),
    [klasse, klassenavn],
  );

  return (
    <div className={css.verticalMargin}>
      <Grid container spacing={3}>
        <DetaljerForm
          dateFromIso={dateFromIso}
          setDateFromIso={setDateFromIso}
          dateToIso={dateToIso}
          setDateToIso={setDateToIso}
          krav={aktivitestkrav}
          setKrav={handleKravChanged}
          estimatedPlacesNeeded={estimatedPlacesNeeded}
          setEstimatedPlacesNeeded={setEstimatedPlacesNeeded}
          klassenavn={klassenavn}
          setKlassenavn={setKlassenavn}
        />
        <FritekstSection fritekst={fritekst} setFritekst={setFritekst} />
        <Handlingsknapper
          isCreating={isCreating}
          opprettDisabled={opprettDisabled}
          activityChanged={activityChanged}
          periodChanged={periodChanged}
          saveHandler={saveHandler}
          deleteHandler={deleteHandler}
          praksisklasseChanged={praksisklasseChanged}
        />
      </Grid>
    </div>
  );
};

const DetaljerForm: React.FC<
  React.PropsWithChildren<{
    dateFromIso: string | undefined;
    setDateFromIso: (newDateFromIso: string) => void;
    dateToIso: string | undefined;
    setDateToIso: (newDateToIso: string) => void;
    krav: AktivitetskravDto | '';
    setKrav: (kravId: string) => void;
    estimatedPlacesNeeded: number | undefined;
    setEstimatedPlacesNeeded: (newEstimatedPlacesNeeded: number) => void;
    klassenavn: string;
    setKlassenavn: (klasseId: string) => void;
  }>
> = ({
  dateFromIso,
  setDateFromIso,
  dateToIso,
  setDateToIso,
  estimatedPlacesNeeded,
  setEstimatedPlacesNeeded,
  krav,
  setKrav,
  klassenavn,
  setKlassenavn,
}) => {
  const { praksisaktivitet } = useActivityContextUHA();
  const { klasse, praksis, emne, muligeKrav, sted } = praksisaktivitet;
  const findBestillerText = () => {
    const stedNavn = sted.navn;
    const antall = sted.navn.length;
    if (antall > 1) {
      return `${stedNavn[antall - 2]} - ${stedNavn[antall - 1]}`;
    }
    return antall === 1 ? stedNavn[0] : '';
  };
  const bestillerText = findBestillerText();

  const labels = useFormatMessageFor(translation);

  const startDate = dayjs(new Date(klasse.undervisningsenhetPeriode.start));
  const endDate = dayjs(new Date(klasse.undervisningsenhetPeriode.slutt));

  return (
    <>
      {/* Bestiller */}
      <Grid item xs={12}>
        <FakeLockedInput
          label={labels.bestiller}
          value={bestillerText}
          dataCy="activity-details-bestiller"
        />
      </Grid>

      {/* emne */}
      <Grid item xs={6}>
        <FakeLockedInput
          label={labels.aktivitetsemnenavn}
          value={emne.navn}
          dataCy="activity-details-emnenavn"
        />
      </Grid>
      <Grid item xs={6}>
        <FakeLockedInput
          label={labels.aktivitetsemnebeskrivelse}
          value={emne.kode}
          dataCy="activity-details-emnebeskrivelse"
        />
      </Grid>

      {/* practice and class */}
      <Grid item xs={6}>
        <FakeLockedInput
          label={labels.aktivitetspraksistype}
          value={praksis.navn}
          dataCy="activity-details-praksistype"
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          label={labels.aktivitetsklasse}
          value={klassenavn}
          htmlId={'select-klasse'}
          onChange={setKlassenavn}
          maxLength={50}
          dataCy="activity-details-praksisklasse-input"
        />
      </Grid>

      {/* aktivitetskrav */}
      {muligeKrav.length > 0 && (
        <>
          <Grid item xs={6}>
            <Dropdown
              id={'aktivitetskrav-select'}
              onChange={(event, value) => value && setKrav(value.id)}
              label={labels.aktivitetskrav}
              options={[
                ...muligeKrav,
                {
                  id: INGEN_AKTIVITETSKRAV.id,
                  navn: INGEN_AKTIVITETSKRAV.navn,
                },
              ]}
              defaultValue={krav ? { ...krav } : undefined}
              placeholder={labels.velgAktivitetskrav}
            />
          </Grid>
          <Grid item xs={6} />
        </>
      )}

      {/* period and estimated need */}
      <Grid item xs={3}>
        {!praksisaktivitet.publisertTilFs ? (
          <DatePicker
            value={dateFromIso}
            onChange={setDateFromIso}
            label={labels.dateFrom}
            constraints={
              dateToIso
                ? {
                    minDate: startDate,
                    maxDate: dayjs(dateToIso),
                  }
                : {
                    minDate: startDate,
                    maxDate: endDate,
                  }
            }
            dataCy="activity-details-start-date-picker"
          />
        ) : (
          <FakeLockedInput
            label={labels.dateFrom}
            value={dateFromIso ? dateFromIso.toString() : ' '}
          />
        )}
      </Grid>
      <Grid item xs={3}>
        {!praksisaktivitet.publisertTilFs ? (
          <DatePicker
            value={dateToIso}
            onChange={setDateToIso}
            label={labels.dateTo}
            constraints={
              dateFromIso
                ? {
                    minDate: dayjs(dateFromIso),
                    maxDate: endDate,
                  }
                : {
                    minDate: startDate,
                    maxDate: endDate,
                  }
            }
            dataCy="activity-details-end-date-picker"
          />
        ) : (
          <FakeLockedInput
            label={labels.dateTo}
            value={dateToIso ? dateToIso.toString() : ' '}
          />
        )}
      </Grid>
      <Grid item xs={3}>
        <NumberInput
          htmlId="estimatedPlaces"
          labelClassName={css.smallInputNumber}
          inputClassName={css.smallInputNumber}
          value={estimatedPlacesNeeded || 0}
          label={labels.estimatedPlacesNeed}
          onChange={(value) => value && setEstimatedPlacesNeeded(value)}
          dataCy="activity-details-estimert-behov-input"
        />
      </Grid>
      <Grid item xs={3} />
    </>
  );
};

type FritekstSectionProps = {
  fritekst: string | undefined;
  setFritekst: (fritekst: string) => void;
};

const MAX_FRITEKST_LENGTH = 2000;

const FritekstSection: React.FC<
  React.PropsWithChildren<FritekstSectionProps>
> = ({ fritekst, setFritekst }) => {
  const { praksisaktivitet } = useActivityContextUHA();
  const { kanEndre } = praksisaktivitet;

  const labels = useFormatMessageFor(translation);

  const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setFritekst(event.currentTarget.value);

  const { callbackRef: wrapperRef, onEvent: onWrapperFocus } =
    useScrollIntoViewOnEvent<HTMLDivElement>();

  const readOnly = useMemo(() => !kanEndre, [kanEndre]);

  if (readOnly)
    return (
      <FritekstSectionReadOnly
        fritekst={fritekst ?? ''}
        title={labels.fritekstTitle}
        subtitle={labels.fritekstSubtitle}
      />
    );

  return (
    <Grid item xs={12}>
      <PanelHeader
        title={labels.fritekstTitle}
        subtitle={labels.fritekstSubtitle}
        collapsible
      >
        <div onFocus={onWrapperFocus} ref={wrapperRef}>
          <Textarea
            htmlId={'activity-fritekst'}
            onChange={onChange}
            maxLength={MAX_FRITEKST_LENGTH}
            placeholder={labels.fritekstPlaceholder}
            className={css.fritekst}
            value={fritekst}
          />
        </div>
      </PanelHeader>
    </Grid>
  );
};

const Handlingsknapper: React.FC<
  React.PropsWithChildren<{
    isCreating: boolean;
    opprettDisabled: boolean;
    activityChanged: boolean;
    periodChanged: boolean;
    saveHandler: () => void;
    deleteHandler: (showSnackbarMsg?: boolean) => void;
    praksisklasseChanged: boolean;
  }>
> = ({
  isCreating,
  opprettDisabled,
  activityChanged,
  periodChanged,
  saveHandler,
  deleteHandler,
  praksisklasseChanged,
}) => {
  const { praksisaktivitet } = useActivityContextUHA();
  const { kanEndre, delt, onskeliste } = praksisaktivitet;

  const [activityChangeConfirmation, setActivityChangeConfirmation] =
    useState(false);
  const [activityDeletionConfirmation, setActivityDeletionConfirmation] =
    useState(false);

  const labels = useFormatMessageFor(translation);

  const save = () => {
    if (!isCreating && periodChanged) {
      setActivityChangeConfirmation(true);
    } else {
      saveHandler();
    }
  };

  const activityChangeConfirmationCloseHandler = (confirmed: boolean) => {
    setActivityChangeConfirmation(false);
    if (confirmed) {
      saveHandler();
    }
  };

  const activityDeletionConfirmationCloseHandler = (confirmed: boolean) => {
    setActivityDeletionConfirmation(false);
    if (confirmed) {
      deleteHandler();
    }
  };

  const PraksisklasseChangedText = () =>
    praksisklasseChanged && (isCreating || (!isCreating && kanEndre)) ? (
      <>
        <Grid item xs={6} />
        <Grid item xs={6}>
          <div className={css.praksisklasseChanged}>
            {labels.praksisklasseChanged}
          </div>
        </Grid>
      </>
    ) : (
      <></>
    );

  const getDeleteConfirmationContent = (): string => {
    if (delt) {
      return onskeliste.aapnet
        ? labels.deleteSharedActivityWithOnskerConfirmationDialogContents
        : labels.deleteSharedActivityConfirmationDialogContents;
    }

    return labels.deleteConfirmationDialogContents;
  };

  return (
    <>
      <Grid item xs={6}>
        <div className={detaljerCss.buttonContainer}>
          {kanEndre && (
            <Button.Tertiary
              className={'delete-button'}
              data-cy={'delete-activity-button'}
              onClick={() => {
                setActivityDeletionConfirmation(true);
              }}
            >
              {labels.delete}
            </Button.Tertiary>
          )}
        </div>
      </Grid>
      <Grid item xs={6}>
        <div
          className={classNames(detaljerCss.buttonContainer, detaljerCss.right)}
        >
          {isCreating && (
            <Button.Secondary
              disabled={opprettDisabled}
              onClick={() => {
                save();
              }}
            >
              {labels.create}
            </Button.Secondary>
          )}
          {!isCreating && kanEndre && (
            <Button.Secondary
              disabled={!activityChanged}
              onClick={() => {
                save();
              }}
              className="saveButton"
              data-cy={'save-activity-button'}
            >
              {labels.save}
            </Button.Secondary>
          )}
        </div>
      </Grid>
      <PraksisklasseChangedText />

      <ConfirmationDialog
        isOpen={activityDeletionConfirmation}
        onClose={activityDeletionConfirmationCloseHandler}
        title={labels.deleteConfirmationDialogTitle}
        dataCy={'delete-praksisaktivitet-confirmation-dialog'}
      >
        <p>{getDeleteConfirmationContent()}</p>
      </ConfirmationDialog>
      <ConfirmationDialog
        isOpen={activityChangeConfirmation}
        onClose={activityChangeConfirmationCloseHandler}
        title={labels.changePeriodConfirmationDialogTitle}
      >
        <p>{labels.changePeriodConfirmationDialogContents}</p>
      </ConfirmationDialog>
    </>
  );
};

export default Detaljer;
